import React, { forwardRef } from 'react'

import { cn } from '@/lib/utils'

type Props = {
  /** Field label — "sales", "work", "Notes". Sits above the value in full white, like the design. */
  label: string
  value: React.ReactNode
  /** Let the value wrap onto as many lines as it needs (notes, addresses) instead of clipping it. */
  multiline?: boolean
  href?: string
  external?: boolean
  className?: string
}

/**
 * One cell of the iOS grouped list: a 17pt label over a 17pt value on 22pt leading.
 * The design is drawn on a 402pt-wide screen and rendered into the 300px phone screen, so
 * 1pt ≈ 0.746px — hence 12.7px type and 16.4px leading. Single-line cells keep the design's
 * fixed 52pt height so the list never resizes when a value is missing.
 * Colours are fixed iOS values on purpose (this is a picture of iOS, not app chrome), so they
 * must not follow the LiveContact light/dark tokens.
 */
const InfoCard = forwardRef<HTMLDivElement, Props>(
  ({ label, value, multiline, href, external, className }, ref) => {
    const valueClass = cn(
      'text-[12.7px] leading-[16.4px] tracking-[-0.32px] text-white/70',
      multiline ? 'whitespace-pre-wrap [overflow-wrap:anywhere]' : 'truncate'
    )

    return (
      <div
        ref={ref}
        className={cn(
          'flex min-w-0 flex-col justify-center',
          multiline ? 'gap-[6px] py-[3px]' : 'h-[38.8px]',
          className
        )}
      >
        {label && (
          <p className="truncate text-[12.7px] leading-[16.4px] tracking-[-0.32px] text-white">
            {label}
          </p>
        )}
        {href ? (
          <a
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className={cn('block', valueClass)}
          >
            {value}
          </a>
        ) : (
          <p className={valueClass}>{value}</p>
        )}
      </div>
    )
  }
)

InfoCard.displayName = 'InfoCard'

export default InfoCard
